import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux'
import { PageTitle, CreateButton } from '../../../components';
import TasksListFilters from './TasksListFilters/TasksListFilters';
import TasksListResume from './TasksListResume/TasksListResume';
import TasksListDetails from './TasksListDetails/TasksListDetails';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { loadTasks, loadPhases, loadUsers, loadPeriods } from '../../../redux';

import './TasksList.css';

const TasksList = ({ screeningId, userId, hideTitle }) => {
    const me                    = useSelector(state => state.auth.user);
    const { tasks }             = useSelector(state => state.tasks);
    const { phases }            = useSelector(state => state.phases);
    const { periods }           = useSelector(state => state.periods);
    const dispatch              = useDispatch();
    const { t }                 = useTranslation();
    const [filters, setFilters] = useState({
        group_type: 'days',
        view_mode: 'details',
        activity_id: null,
        status: null,
        user_id: userId || ( me?.can('assign', 'tasks') ?  undefined : me.id),
        start_at_gte: moment(),
        // increase dates interval if the current user doesn't have access
        // to other users tasks
        start_at_lte: moment().add(me?.can('assign', 'tasks') && !userId ? 1 : 6, 'month'),
        screening_id: screeningId,
        sector_id: null,
        phase_id: null,
        period_id: null
    });

    useEffect(() => {
        if (me?.can('readOther', 'phases') || me?.can('readSelf', 'phases'))
            dispatch(loadPhases());
        if (me?.can('readOther', 'users') || me?.can('readSelf', 'users'))
            dispatch(loadUsers());
        if (me?.can('readOther', 'periods') || me?.can('readSelf', 'periods'))
            dispatch(loadPeriods());
    }, []);

    useEffect(() => {
        if (userId && userId !== filters?.user_id) {
            setFilters({
                ...filters,
                user_id: userId,
                needsRemoteUpdate: true
            });
        }
    }, [userId])

    useEffect(() => {
        setFilters({
            ...filters,
            screening_id: screeningId
        })
    }, [screeningId])

    useEffect(() => {
        if (filters.needsRemoteUpdate) {
            setFilters({ ...filters, needsRemoteUpdate: false });
            dispatch(loadTasks(filters));
        }
    }, [filters?.needsRemoteUpdate])

    const on_change_filters = (values) => {
        values.needsRemoteUpdate = filters.view_mode === values.view_mode && filters.group_type === values.group_type;
        setFilters(values)
    }

    const tasksUsers = React.useMemo(() => {
        return _.uniqBy(tasks?.map(t => {
            if (t.user)
                return t.user
        }), 'id').filter(f => !!f);
    }, [tasks]);

    const filteredActivities = React.useMemo(() => {
        return _.uniqBy(tasks?.map(t => {
            return t.activity
        }), 'id');
    }, [tasks]);

    const screenings = React.useMemo(() => {
        return _.uniqBy(tasks?.map(t => {
            if (t.screening)
                return t.screening
        }), 'id').filter(s => !!s);
    }, [tasks]);

    const shifts = React.useMemo(() => {
        return _.uniqBy(tasks?.map(t => {
            if (t.shift)
                return t.shift;
        }), 'id').filter(s => !!s);
    }, [tasks]);

    const sectors = React.useMemo(() => {
        return _.uniqBy(tasks?.map(t => {
            if (t.shift?.sector)
                return t.shift.sector;
        }), 'id').filter(s => !!s);
    }, [tasks]);

    const actions =
        <CreateButton
            module="tasks"
            text="tasks.add"
            customPath={`/tasks/new/${screeningId || ""}`}
        />

    const title = <span>{t("tasks.task", {count: 42})}</span>;

    if (!tasks || !me)
        return null;

    return (
        <div className="TasksList">
            {me &&
                <>
                    {!hideTitle && (
                        <div className="noPrint">
                            <PageTitle icon="id-badge" title={title} actions={actions} />
                        </div>
                    )}
                    <TasksListFilters
                        defaultFilters={filters}
                        userId={userId}
                        users={tasksUsers}
                        screenings={screenings}
                        phases={phases}
                        periods={periods}
                        shifts={shifts}
                        sectors={sectors}
                        activities={filteredActivities}
                        onChange={on_change_filters}
                        screening_id={screeningId}
                    />
                    {filters.view_mode === 'resume' &&
                        <TasksListResume tasks={tasks} filters={filters} />
                    }
                    {filters.view_mode === 'details' &&
                        <TasksListDetails
                            tasks={tasks}
                            filters={filters}
                            screenings={screenings}
                            phases={phases}
                            periods={periods}
                            shifts={shifts}
                            sectors={sectors}
                            me={me}
                        />
                    }
                </>
            }
        </div>
    );
}

export default TasksList;
